<script setup>
import { computed, inject } from 'vue'

import ButtonComponent from '@/Components/ButtonComponent.vue'
import SvgVue from '@/Components/SvgVue.vue'

import useCart from '../useCart.js'

const emit = defineEmits(['removeDiscount'])

const cart = useCart(inject('cart'))
const cartModel = cart.cart
const checkoutOptions = inject('checkoutOptions')

const activeDiscount = computed(() => {
  return cartModel.value.discount || null
})
</script>

<template>
  <div class="my-3">
    <template v-if="cart.cartItem.value.quantity > 1">
      <div class="flex items-center py-1">
        <span>{{
          /* translators: Represents unit price label. Shown as part of the checkout summary table. */
          $t('lemonsqueezy_checkout.unit_price')
        }}</span>
        <span class="ml-auto" dusk="checkout-form-unit-price">
          <template v-if="cart.cartItem.value.price_model.is_tiered">{{
            /* translators: Represents product price, as in `Price starts from $59` */
            $t('lemonsqueezy_checkout.product_price.from', {
              price: formatCurrency(cart.cartItem.value.unit_price, cartModel.currency),
            })
          }}</template>
        </span>
      </div>
      <div class="flex items-center py-1">
        <span>{{
          /* translators: Represents cart item quantity label. Shown as part of the checkout summary table. */
          $t('lemonsqueezy_checkout.quantity')
        }}</span>
        <span class="ml-auto" dusk="checkout-form-quantity">
          {{ formatNumber(cart.cartItem.value.quantity) }}
        </span>
      </div>
      <div v-if="cart.cartItem.value.fixed_fee" class="flex items-center py-1">
        <span>
          {{
            /* translators: Represents fixed fee amount label. Shown as part of the checkout summary table. */
            $t('lemonsqueezy_checkout.fixed_fee')
          }}
        </span>
        <span class="ml-auto" dusk="checkout-form-fixed-fee">
          {{ formatCurrency(cart.cartItem.value.fixed_fee, cartModel.currency) }}
        </span>
      </div>
    </template>
    <div v-if="cart.hasSetupFee()" class="flex items-center py-1">
      <span>
        {{
          /* translators: Represents setup fee value label. Shown in the checkout summary table. */
          $t('lemonsqueezy_checkout.setup_fee')
        }}
      </span>
      <span class="ml-auto" dusk="checkout-form-setup-price">
        {{ formatCurrency(cartModel.setup_fee, cartModel.currency) }}
      </span>
    </div>
    <div class="flex items-center py-1">
      <span>{{
        /* translators: Represents checkout subtotal amount label. Shown as part of the checkout summary table */
        $t('lemonsqueezy_checkout.subtotal')
      }}</span>
      <span class="ml-auto" dusk="checkout-form-subtotal-price">
        {{ formatCurrency(cartModel.subtotal, cartModel.currency) }}
      </span>
    </div>
    <div v-if="activeDiscount" class="flex items-center py-0.5" dusk="checkout-form-discount-row">
      <span class="flex items-center overflow-hidden">
        {{ $t('lemonsqueezy_checkout.discount') }}
        <span
          class="ml-1 inline-block rounded bg-wedges-gray-50 px-[10px] py-[2px] font-mono text-12 uppercase tracking-widest text-wedges-gray-900"
        >
          <span class="block max-w-30 truncate">
            {{ activeDiscount.code }}
          </span>
        </span>
        <ButtonComponent
          v-if="checkoutOptions.discount"
          variant="plain"
          dusk="checkout-form-remove-discount"
          @click="emit('removeDiscount')"
        >
          <SvgVue icon="Cross Small" class="size-3 stroke-current text-wedges-gray-400" />
        </ButtonComponent>
      </span>
      <span class="ml-auto" dusk="checkout-form-discount-price">
        -{{ formatCurrency(cartModel.discount_total, cartModel.currency) }}
      </span>
    </div>
    <div v-if="activeDiscount && $parent.form.errors.discount_code" class="flex items-center py-0.5">
      <p class="text-wtf-paradise">
        {{ $parent.form.errors.discount_code }}
      </p>
    </div>
    <div v-if="cartModel.tax_name && cartModel.tax > 0" class="flex items-center py-1" dusk="checkout-form-tax-row">
      <span>
        <template v-if="cartModel.tax_inclusive">
          {{
            $t('lemonsqueezy_checkout.tax_name_tax_rate_included_in_total', {
              taxName: cartModel.tax_name,
              taxRate: formatPercentage(cartModel.tax_rate),
            })
          }}
        </template>
        <template v-else>
          {{
            /* translators: Represents local tax name + tax rate, for example `PDV 20%`. Shown in the checkout summary table. */
            $t('lemonsqueezy_checkout.tax_name_tax_rate', {
              taxName: cartModel.tax_name,
              taxRate: formatPercentage(cartModel.tax_rate),
            })
          }}
        </template>
      </span>
      <span class="ml-auto" dusk="checkout-form-tax-price">
        {{ formatCurrency(cartModel.tax, cartModel.currency) }}
      </span>
    </div>
    <div class="flex items-center py-0.75 text-16 font-medium">
      <span>{{
        /* translators: Represents checkout total amount label. Shown as part of the checkout summary table. */
        $t('lemonsqueezy_checkout.total')
      }}</span>
      <span class="ml-auto" dusk="checkout-form-total-price">
        {{ formatCurrency(cartModel.total, cartModel.currency) }}
      </span>
    </div>
  </div>
</template>
